<template>
  <view class="lanhu-tabbar">
    <view class="tabbar-shell">
      <view
        v-for="item in tabs"
        :key="item.key"
        class="tab-slot"
        :class="{ active: current === item.key, 'fab-slot': item.key === 'fab' }"
        @tap="onTab(item.key)"
      >
        <template v-if="item.key !== 'fab'">
          <view class="tab-inner" :class="{ 'is-active': current === item.key }">
            <image
              class="tab-icon"
              :class="{ 'is-active': current === item.key }"
              :src="item.icon"
              mode="aspectFit"
              :lazy-load="false"
            />
            <text class="tab-text">{{ t(item.labelKey!) }}</text>
          </view>
        </template>
      </view>

      <!-- 中间 logo 64px + 阴影；弹层打开切关闭态，旋转带弹簧阻尼 -->
      <view class="center-fab" :class="{ 'is-close': menuOpen }" @tap.stop="onCenter">
        <view class="fab-spin">
          <image class="fab-logo" :src="icons.fabLogo" mode="aspectFit" :lazy-load="false" />
          <view class="fab-close" aria-hidden="true">
            <text class="fab-close-x">×</text>
          </view>
        </view>
      </view>
    </view>
  </view>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { isUserAuthenticated, triggerLogin } from '@/common/auth';

const { t } = useI18n();

const props = defineProps<{ current: string; menuOpen?: boolean }>();
const emit = defineEmits<{
  (e: 'change', key: string): void
  (e: 'tradeMenuOpen'): void
  (e: 'tradeMenuClose'): void
}>();

const TAB = '/static/image/lanhu/tabbar';
const icons = {
  /** 中间 FAB：AI@2x.webp，展示 64×64 */
  fabLogo: `${TAB}/ai-fab.webp`,
};

const tabs: Array<{
  key: string
  labelKey?: string
  icon?: string
}> = [
  {
    key: 'home',
    labelKey: 'tabbar.home',
    icon: `${TAB}/home.webp`,
  },
  {
    key: 'trade',
    labelKey: 'tabbar.trade',
    icon: `${TAB}/trade.webp`,
  },
  { key: 'fab' },
  {
    key: 'fund',
    labelKey: 'tabbar.fund',
    icon: `${TAB}/fund.webp`,
  },
  {
    key: 'assets',
    labelKey: 'tabbar.assets',
    icon: `${TAB}/assets.webp`,
  },
];

const onTab = (key: string) => {
  if (key === 'fab') return;
  emit('change', key);
};

/**
 * 中间 FAB（对齐 green）：
 * - 弹层已开 → 关闭
 * - 未登录 → 登录
 * - 否则交给 layout：非快捷页直达默认项 / 已在快捷页则打开弹层
 */
const onCenter = () => {
  if (props.menuOpen) {
    emit('tradeMenuClose');
    return;
  }
  if (!isUserAuthenticated()) {
    triggerLogin();
    return;
  }
  emit('tradeMenuOpen');
};
</script>

<style scoped lang="scss">
/*
 * Lanhu workspace group_21：
 * 外壳 702×132 / radius 69 / 左右距屏 24
 * 内边距 12 → 选中胶囊 box_2：146×108 / radius 54 / #f4f6f8
 * FAB：AI logo 64px + 底部柔和阴影；弹层打开时同尺寸黑底 ×
 */
.lanhu-tabbar {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 10px;
  z-index: 10030;
  /* min(48rpx,48px)：窄屏按设计 48rpx，宽屏锁 48px，与快捷菜单同宽 */
  width: calc(100% - min(48rpx, 48px));
  max-width: calc(var(--app-max-width, 750px) - 48px);
  height: 180rpx;
  overflow: visible;
  pointer-events: none;
  box-sizing: border-box;
}

.tabbar-shell {
  pointer-events: auto;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 132rpx;
  padding: 10rpx;
  border-radius: 69rpx;
  border: 1rpx solid #e8ecf1;
  background: #ffffff;
  /* 阴影向下，营造底栏浮起的立体感 */
  box-shadow: 0 8rpx 8rpx rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  box-sizing: border-box;
  overflow: visible;
}

.tab-slot {
  flex: 1;
  min-width: 0;
  height: 108rpx;
  display: flex;
  align-items: center;
  justify-content: center;

  &.fab-slot {
    flex: 0 0 128rpx;
    width: 128rpx;
    pointer-events: none;
  }

  .tab-inner {
    width: 100%;
    max-width: 146rpx;
    height: 108rpx;
    border-radius: 54rpx;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12rpx;
    box-sizing: border-box;
    padding: 0 8rpx;
    transition: transform 0.15s ease, background-color 0.2s ease;

    &.is-active {
      width: 146rpx;
      max-width: 146rpx;
      background: #f4f6f8;
    }

    &:active {
      transform: scale(0.96);
    }
  }

  .tab-icon {
    width: 32rpx;
    height: 32rpx;
    flex-shrink: 0;

    /* 选中：默认灰图标直接染黑 */
    &.is-active {
      filter: brightness(0);
    }
  }

  .tab-text {
    font-family: 'Noto Sans SC', sans-serif;
    font-size: 22rpx;
    line-height: 22rpx;
    color: #8e96a3;
    text-align: center;
    white-space: nowrap;
  }

  &.active {
    .tab-text {
      color: #24272d;
      font-weight: 500;
    }
  }
}

.center-fab {
  pointer-events: auto;
  position: absolute;
  left: 50%;
  top: -28px;
  transform: translateX(-50%);
  width: 128rpx;
  height: 128rpx;
  z-index: 2;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: visible;
  transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:active {
    transform: translateX(-50%) scale(0.94);
  }

  .fab-spin {
    position: relative;
    width: 128rpx;
    height: 128rpx;
    border-radius: 50%;
    /* 旋转 + 过冲：弹簧阻尼感 */
    transition: transform 0.48s cubic-bezier(0.22, 1.55, 0.36, 1);
    transform: rotate(0deg);
    will-change: transform;
    /* 参考设计稿：logo 下方柔和投影落到白底栏上 */
    filter: drop-shadow(0 5px 8px rgba(0, 0, 0, 0.333));
  }

  .fab-logo,
  .fab-close {
    position: absolute;
    left: 0;
    top: 0;
    width: 128rpx;
    height: 128rpx;
    border-radius: 50%;
    box-sizing: border-box;
    transition:
      opacity 0.28s ease,
      transform 0.48s cubic-bezier(0.22, 1.55, 0.36, 1);
  }

  .fab-logo {
    display: block;
    opacity: 1;
    transform: rotate(0deg) scale(1);
    z-index: 1;
  }

  .fab-close {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000000;
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.22);
    opacity: 0;
    transform: rotate(-90deg) scale(0.86);
    pointer-events: none;
    z-index: 2;
  }

  .fab-close-x {
    color: #ffffff;
    font-size: 36px;
    line-height: 1;
    font-weight: 300;
    margin-top: -6px;
  }

  &.is-close {
    .fab-spin {
      transform: rotate(90deg);
      filter: none;
    }

    .fab-logo {
      opacity: 0;
      transform: rotate(0deg) scale(0.86);
    }

    .fab-close {
      opacity: 1;
      /* 抵消外层 90deg，让 × 正向显示，并带过冲回弹 */
      transform: rotate(-90deg) scale(1);
    }
  }
}
</style>
